import {
  Table,
  TableHeader,
  TableBody,
  TableColumn,
  TableRow,
  TableCell,
  Chip,
  Selection,
  Button,
  Dropdown,
  DropdownTrigger,
  DropdownMenu,
  DropdownItem,
  Tooltip,
} from '@nextui-org/react';
import { Settlement } from 'common/src/utils/types';
import { formatMoneyUSD } from '../../../utils/functions';
import { Icon } from '@iconify/react';

interface SettlementsTableProps {
  settlements: Settlement[];
  selectionMode?: 'single' | 'multiple' | 'none';
  selectedKeys?: Selection;
  onSelectionChange?: (keys: Selection) => void;
  onEdit?: (settlement: Settlement) => void;
  onDelete?: (settlement: Settlement) => void;
  onApprove?: (settlement: Settlement) => void;
  bottomContent?: React.ReactNode;
}

export const SettlementsTable = ({
  settlements,
  selectionMode = 'none',
  selectedKeys,
  onSelectionChange,
  onEdit,
  onDelete,
  onApprove,
  bottomContent,
}: SettlementsTableProps) => {
  const columns = [
    { name: 'ACCIONES', uid: 'actions' },
    { name: 'ESTADO', uid: 'upload_status' },
    { name: 'ID', uid: 'id' },
    { name: 'EXPORTADORA', uid: 'exporter' },
    { name: 'ESPECIE', uid: 'species_id' },
    { name: 'REFERENCIA', uid: 'reference_number' },
    { name: 'CLIENTE', uid: 'receiver' },
    { name: 'MERCADO VTA', uid: 'market_id' },
    { name: 'FECHA VTA', uid: 'date_sold' },
    { name: 'MERCADO COMPARABLE', uid: 'comparable_market_id' },
    { name: 'CONTENEDOR', uid: 'container_number' },
    { name: 'NAVE', uid: 'ship' },
    { name: 'ETA', uid: 'eta' },
    { name: 'ETD', uid: 'etd' },
    { name: 'POD', uid: 'pod' },
    { name: 'Temporada', uid: 'season' },
    { name: 'INCOTERM ORIG.', uid: 'original_incoterm' },
    { name: 'MONEDA', uid: 'local_currency' },
    { name: 'TIPO DE CAMBIO', uid: 'exchange_rate' },
    { name: 'FLETE USD', uid: 'total_freight_usd' },
    { name: 'FOB USD', uid: 'total_fob' },
    { name: 'KILOS', uid: 'total_kilos' },
    { name: 'FOB/CAJA EQ.', uid: 'fob_per_box' }

  ];

  const renderCell = (settlement: Settlement, columnKey: string) => {
    const totalKilos = settlement.settlement_details?.reduce(
      (acc, detail) => acc + (detail.kilos || 0),
      0
    );

    const totalFob = settlement.settlement_details?.reduce(
      (acc, detail) => acc + (detail.usd_total_fob || 0),
      0
    );

    const fobPerBox = totalKilos > 0 ? (totalFob / totalKilos) * 5 : 0;

    switch (columnKey) {
      case 'id':
        return settlement.id;
      case 'season':
        return settlement.season;
      case 'species_id':
        return settlement.species?.name || '-';
      case 'exporter':
        return settlement.exporter?.name || '-';
      case 'reference_number':
        return settlement.reference_number;
      case 'original_incoterm':
        return settlement.original_incoterm;
      case 'exchange_rate':
        return settlement.exchange_rate;
      case 'receiver':
        return settlement.receiver;
      case 'region':
        return settlement.region;
      case 'comparable_market_id':
        return settlement.comparable_market_id;
      case 'market_id':
        return settlement.market_id;
      case 'eta':
        return settlement.ship_trip?.eta.split('T')[0];
      case 'etd':
        return settlement.ship_trip?.etd.split('T')[0];
      case 'pod':
        return settlement.ship_trip?.market_id;
      case 'container_number':
        return settlement.container_number;
      case 'ship':
        return settlement.ship_trip?.ship?.name || '-';
      case 'date_sold':
        return new Date(settlement.date_sold).toLocaleDateString('es-CL');
      case 'local_currency':
        return settlement.local_currency;
      case 'total_fob':
        return formatMoneyUSD(totalFob);
      case 'total_freight_usd':
        return formatMoneyUSD(settlement.total_freight_usd);
      case 'total_kilos':
        return totalKilos.toLocaleString('es-CL');
      case 'fob_per_box':
        return formatMoneyUSD(fobPerBox);
      case 'upload_status':
        return (
          <Chip
            className='capitalize'
            color={
              settlement.upload_status === 'approved' ? 'success' : 'warning'
            }
            size='sm'
            variant='flat'
          >
            {settlement.upload_status || 'pending'}
          </Chip>
        );
      case 'actions':
        return (
          <div className='flex gap-2 justify-center'>
            <Tooltip
              content={
                settlement.upload_status === 'approved'
                  ? 'Marcar como pendiente'
                  : 'Aprobar liquidación'
              }
            >
              <Button
                isIconOnly
                size='sm'
                variant='light'
                color={
                  settlement.upload_status === 'approved'
                    ? 'warning'
                    : 'success'
                }
                onPress={() => onApprove?.(settlement)}
              >
                {settlement.upload_status === 'approved' ? (
                  <Icon icon='tabler:x' className='text-warning-500' />
                ) : (
                  <Icon icon='tabler:check' className='text-success-500' />
                )}
              </Button>
            </Tooltip>
            <Dropdown>
              <DropdownTrigger>
                <Button isIconOnly size='sm' variant='light'>
                  <Icon icon='tabler:dots-vertical' className='text-default-500' />
                </Button>
              </DropdownTrigger>
              <DropdownMenu>
                <DropdownItem onPress={() => onEdit?.(settlement)}>
                  Editar
                </DropdownItem>
                <DropdownItem
                  className='text-danger'
                  color='danger'
                  onPress={() => onDelete?.(settlement)}
                >
                  Eliminar
                </DropdownItem>
              </DropdownMenu>
            </Dropdown>
          </div>
        );
      default:
        return null;
    }
  };

  return (
    <div className='w-full'>
      <Table
        aria-label='Tabla de Liquidaciones'
        selectionMode={selectionMode}
        selectedKeys={selectedKeys}
        onSelectionChange={onSelectionChange}
        bottomContent={bottomContent}
        classNames={{
          wrapper: 'min-h-[400px]',
        }}
      >
        <TableHeader columns={columns}>
          {(column) => (
            <TableColumn
              key={column.uid}
              align={column.uid === 'actions' ? 'center' : 'start'}
            >
              {column.name}
            </TableColumn>
          )}
        </TableHeader>
        <TableBody items={settlements}>
          {(settlement) => (
            <TableRow key={settlement.id}>
              {(columnKey) => (
                <TableCell>
                  {renderCell(settlement, columnKey as string)}
                </TableCell>
              )}
            </TableRow>
          )}
        </TableBody>
      </Table>
    </div>
  );
};

export default SettlementsTable;
